import React, { useState, useContext } from 'react';
import { format } from 'date-fns';
import './MatchGroup.css';
import { createPortal } from 'react-dom';
import MatchItemTCG from './MatchItemTCG';
import MatchItemVGC from './MatchItemVGC';

const MatchGroupItem = ({ group, onEdit, onDelete, onShare }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [selectedMatchType, setSelectedMatchType] = useState(null);
  const [currentTCGPage, setCurrentTCGPage] = useState(1);
  const [currentVGCPage, setCurrentVGCPage] = useState(1);
  const matchesPerPage = 8;

  // Safely check for valid group data
  if (!group || !group.matches) {
    return (
      <div className="match-group error">
        <div className="match-group-header">
          <h3 className="match-group-title">Invalid Group Data</h3>
        </div>
      </div>
    );
  }

  // Calculate stats for the match group
  const calculateStats = () => {
    const tcgMatches = Array.isArray(group.matches?.tcg) ? group.matches.tcg : [];
    const vgcMatches = Array.isArray(group.matches?.vgc) ? group.matches.vgc : [];
    const totalMatches = tcgMatches.length + vgcMatches.length;
    
    let wins = 0;
    let losses = 0;
    let ties = 0;

    // Count TCG match results
    tcgMatches.forEach(match => {
      if (!match || !Array.isArray(match.games) || match.games.length === 0) return;
      
      const matchWins = match.games.filter(game => game.result === 'win').length;
      const matchLosses = match.games.filter(game => game.result === 'loss').length;
      
      if (matchWins > matchLosses) wins++;
      else if (matchLosses > matchWins) losses++;
      else ties++;
    });

    // Count VGC match results
    vgcMatches.forEach(match => {
      if (!match) return;
      
      if (match.result === 'win') wins++;
      else if (match.result === 'loss') losses++;
      else if (match.result === 'tie') ties++;
    });

    const winRate = totalMatches > 0 ? Math.round((wins / totalMatches) * 100) : 0;

    return { totalMatches, wins, losses, ties, winRate };
  };

  const stats = calculateStats();

  const formatDate = (dateString) => {
    try {
      if (!dateString) return 'Unknown date';
      
      const date = new Date(dateString);
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'Unknown date';
      }
      
      return format(date, 'MMM d, yyyy');
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Unknown date';
    }
  };

  // Function to open the matches modal
  const handleShowMatches = (e) => {
    e.stopPropagation();
    setIsModalOpen(true);
  };

  // Function to close the matches modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedMatch(null);
    setSelectedMatchType(null);
    // Reset pagination when closing
    setCurrentTCGPage(1);
    setCurrentVGCPage(1);
  };
  
  // Function to go back to match list from match detail view
  const handleBackToList = () => {
    setSelectedMatch(null);
    setSelectedMatchType(null);
  };
  
  // Pagination handlers
  const handleTCGPageChange = (newPage) => {
    setCurrentTCGPage(newPage);
  };
  
  const handleVGCPageChange = (newPage) => {
    setCurrentVGCPage(newPage);
  };

  // Function to render TCG match
  const renderTCGMatch = (match, index) => {
    if (!match || typeof match !== 'object') {
      return null;
    }
    
    // Calculate match result for display
    let matchResult = 'unknown';
    let scoreDisplay = '';
    
    if (Array.isArray(match.games) && match.games.length > 0) {
      const wins = match.games.filter(g => g.result === 'win').length;
      const losses = match.games.filter(g => g.result === 'loss').length;
      
      if (wins > losses) matchResult = 'win';
      else if (losses > wins) matchResult = 'loss';
      else matchResult = 'tie';
      
      scoreDisplay = `${wins}-${losses}`;
    }
    
    // Determine deck sprite sources for user (primary and secondary)
    const userDeckSprite = match.deckSprite?.url || 
                         (match.deck?.pokemonSprite?.url) || 
                         'https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/25.png';
    
    const userSecondaryDeckSprite = match.secondaryDeckSprite?.url || 
                                  (match.deck?.secondaryPokemonSprite?.url) || 
                                  null;
    
    const opponentDeckSprite = match.opponentDeckSprite?.url || 
                             'https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/150.png';
                             
    const opponentSecondaryDeckSprite = match.opponentSecondaryDeckSprite?.url || null;
    
    // Determine deck name
    const userDeckName = match.activeDeck && Array.isArray(match.activeDeck) && match.activeDeck.length > 0 
                          ? match.activeDeck[0] 
                          : typeof match.activeDeck === 'string' 
                            ? match.activeDeck 
                            : 'My Deck';
    
    return (
      <div 
        key={match._id || `tcg-match-${index}`} 
        className="match-list-item enhanced"
        style={{
          borderLeft: `3px solid var(--${matchResult}-color, #ccc)`
        }}
        onClick={(e) => {
          e.stopPropagation();
          // Select this match for viewing in detail
          setSelectedMatch(match);
          setSelectedMatchType('tcg');
        }}
      >
        <div className="match-sprite-team">
          <img 
            src={userDeckSprite} 
            alt="Your Deck" 
            className="deck-sprite"
          />
          {userSecondaryDeckSprite && (
            <img 
              src={userSecondaryDeckSprite} 
              alt="Your Secondary Deck" 
              className="deck-sprite secondary"
            />
          )}
        </div>
        <div className="match-info-enhanced">
          <div className="match-teams">
            <span className="my-deck">{userDeckName}</span>
            <span className="vs-text">vs</span>
            <span className="opponent-deck">{match.opponentDeck || 'Opponent Deck'}</span>
          </div>
          <div className="match-meta">
            <span className="match-date">
              <i className="far fa-calendar-alt"></i> {formatDate(match.createdAt || match.date)}
            </span>
            {match.format && (
              <span className="match-format">
                <i className="fas fa-gamepad"></i> {match.format}
              </span>
            )}
          </div>
        </div>
        <div className="match-result-badge">
          <div className="opponent-sprite-team">
            <img 
              src={opponentDeckSprite} 
              alt="Opponent Deck" 
              className="deck-sprite opponent"
            />
            {opponentSecondaryDeckSprite && (
              <img 
                src={opponentSecondaryDeckSprite} 
                alt="Opponent Secondary Deck" 
                className="deck-sprite opponent secondary"
              />
            )}
          </div>
          {Array.isArray(match.games) && match.games.length > 0 && (
            <span className={`badge badge-${matchResult}`}>
              {scoreDisplay}
            </span>
          )}
        </div>
      </div>
    );
  };

  // Function to render VGC match
  const renderVGCMatch = (match, index) => {
    if (!match || typeof match !== 'object') {
      return null;
    }
    
    // Safely extract match result
    const resultClass = match.result || 'unknown';
    let resultText = '?';
    
    if (match.result === 'win') resultText = 'W';
    else if (match.result === 'loss') resultText = 'L';
    else if (match.result === 'tie') resultText = 'T';
    
    // Determine team sprites - use first Pokemon in the team array if available
    const playerTeamPokemon = match.playerTeamPokemon && Array.isArray(match.playerTeamPokemon) && match.playerTeamPokemon.length > 0
      ? match.playerTeamPokemon
      : [{ sprite: 'https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/25.png' }];
    
    const opponentTeamPokemon = match.opponentTeamPokemon && Array.isArray(match.opponentTeamPokemon) && match.opponentTeamPokemon.length > 0
      ? match.opponentTeamPokemon
      : [{ sprite: 'https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/150.png' }];
    
    return (
      <div 
        key={match._id || `vgc-match-${index}`} 
        className="match-list-item enhanced vgc"
        style={{
          borderLeft: `3px solid var(--${resultClass}-color, #ccc)`
        }}
        onClick={(e) => {
          e.stopPropagation();
          // Select this match for viewing in detail
          setSelectedMatch(match);
          setSelectedMatchType('vgc');
        }}
      >
        <div className="match-sprite-team">
          {playerTeamPokemon.slice(0, 2).map((pokemon, i) => (
            <img 
              key={`my-team-${i}`}
              src={pokemon.sprite || `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/${25 + i}.png`}
              alt="My Team"
              className="team-sprite"
            />
          ))}
        </div>
        <div className="match-info-enhanced">
          <div className="match-teams">
            <span className="my-deck">{match.playerTeam || 'My Team'}</span>
            <span className="vs-text">vs</span>
            <span className="opponent-deck">{match.opponentTeam || match.opponentName || 'Opponent'}</span>
          </div>
          <div className="match-meta">
            <span className="match-date">
              <i className="far fa-calendar-alt"></i> {formatDate(match.date || match.createdAt)}
            </span>
            {match.format && (
              <span className="match-format">
                <i className="fas fa-gamepad"></i> {match.format}
              </span>
            )}
          </div>
        </div>
        <div className="match-result-badge">
          <div className="opponent-sprite-team">
            {opponentTeamPokemon.slice(0, 2).map((pokemon, i) => (
              <img 
                key={`opp-team-${i}`}
                src={pokemon.sprite || `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/${150 + i}.png`}
                alt="Opponent Team"
                className="team-sprite opponent"
              />
            ))}
          </div>
          <span className={`badge badge-${resultClass}`}>
            {resultText}
          </span>
        </div>
      </div>
    );
  };

  // Render the main component
  return (
    <div className="match-group">
      <div className="match-group-header">
        <div className="title-section">
          <h3 className="match-group-title">
            {group.name}
            {group.isShared && (
              <span className="shared-indicator" title="This group is shared">
                <i className="fas fa-users"></i>
              </span>
            )}
            {group.userRole === 'shared' && (
              <span className="shared-with-me-indicator" title="Shared with you">
                <i className="fas fa-share-alt"></i>
              </span>
            )}
          </h3>
        </div>
        <div className="match-group-actions">
          <button
            className="match-group-action"
            onClick={() => onEdit(group)}
            title="Edit Group"
          >
            <i className="fas fa-edit"></i>
          </button>
          {onShare && (
            <button
              className="match-group-action share-btn"
              onClick={() => onShare(group)}
              title="Share Group"
            >
              <i className="fas fa-share-alt"></i>
            </button>
          )}
          <button
            className="match-group-action"
            onClick={() => onDelete(group._id)}
            title="Delete Group"
          >
            <i className="fas fa-trash"></i>
          </button>
        </div>
      </div>
      
      <div className="match-group-content">
        {group.description && (
          <div className="match-group-description">{group.description}</div>
        )}
        
        <div className="match-group-stats">
          <div className="match-group-stat" key="stat-matches">
            <span className="match-group-stat-value">{stats.totalMatches}</span>
            <span className="match-group-stat-label">Matches</span>
          </div>
          <div className="match-group-stat" key="stat-wins">
            <span className="match-group-stat-value">{stats.wins}</span>
            <span className="match-group-stat-label">Wins</span>
          </div>
          <div className="match-group-stat" key="stat-losses">
            <span className="match-group-stat-value">{stats.losses}</span>
            <span className="match-group-stat-label">Losses</span>
          </div>
          {stats.ties > 0 && (
            <div className="match-group-stat" key="stat-ties">
              <span className="match-group-stat-value">{stats.ties}</span>
              <span className="match-group-stat-label">Ties</span>
            </div>
          )}
          <div className="match-group-stat" key="stat-winrate">
            <span className="match-group-stat-value">{stats.winRate}%</span>
            <span className="match-group-stat-label">Win Rate</span>
          </div>
        </div>
      </div>
      
      <div className="match-group-expand" onClick={handleShowMatches}>
        Show Matches
        <i className="fas fa-external-link-alt" style={{ marginLeft: '0.5rem' }}></i>
      </div>

      {/* Matches Modal */}
      {isModalOpen && createPortal(
        <div className="match-modal-overlay" onClick={handleCloseModal}>
          <div className="match-modal-container" onClick={e => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="match-modal-header">
              <h2>
                {selectedMatch 
                  ? `${group.name} - Match Details` 
                  : `${group.name} - Matches`}
              </h2>
              <button onClick={handleCloseModal} className="match-modal-close-btn">×</button>
            </div>
            
            {/* Modal Content */}
            <div className="match-modal-content">
              {selectedMatch ? (
                // Match Detail View
                <div className="match-detail-container">
                  {/* Back button */}
                  <button 
                    className="btn btn-secondary btn-sm" 
                    onClick={handleBackToList}
                    style={{ marginBottom: '1rem' }}
                  >
                    <i className="fas fa-arrow-left" style={{ marginRight: '0.5rem' }}></i>
                    Back to Match List
                  </button>
                  
                  {/* Render the selected match */}
                  {selectedMatchType === 'tcg' ? (
                    <MatchItemTCG match={selectedMatch} />
                  ) : (
                    <MatchItemVGC match={selectedMatch} />
                  )}
                </div>
              ) : (
                // Match List View
                <>
                  {(group.matches?.tcg?.length > 0 || group.matches?.vgc?.length > 0) ? (
                    <div className="match-modal-tabs">
                      {/* Tabbed content for TCG and VGC */}
                      <div className="match-modal-tab-content">
                        {group.matches?.tcg?.length > 0 && (
                          <div className="match-modal-section">
                            <h3 className="match-modal-section-title">TCG Matches ({group.matches.tcg.length})</h3>
                            <div className="match-modal-matches">
                              {/* Paginated TCG matches */}
                              {group.matches.tcg
                                .slice(
                                  (currentTCGPage - 1) * matchesPerPage,
                                  currentTCGPage * matchesPerPage
                                )
                                .map(renderTCGMatch)}
                            </div>
                            
                            {/* Pagination controls for TCG */}
                            {group.matches.tcg.length > matchesPerPage && (
                              <div className="pagination-controls">
                                <button 
                                  className="pagination-button"
                                  onClick={() => handleTCGPageChange(Math.max(1, currentTCGPage - 1))}
                                  disabled={currentTCGPage === 1}
                                >
                                  <i className="fas fa-chevron-left"></i> Previous
                                </button>
                                <span className="pagination-info">
                                  Page {currentTCGPage} of {Math.ceil(group.matches.tcg.length / matchesPerPage)}
                                </span>
                                <button 
                                  className="pagination-button"
                                  onClick={() => handleTCGPageChange(Math.min(Math.ceil(group.matches.tcg.length / matchesPerPage), currentTCGPage + 1))}
                                  disabled={currentTCGPage === Math.ceil(group.matches.tcg.length / matchesPerPage)}
                                >
                                  Next <i className="fas fa-chevron-right"></i>
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {group.matches?.vgc?.length > 0 && (
                          <div className="match-modal-section">
                            <h3 className="match-modal-section-title">VGC Matches ({group.matches.vgc.length})</h3>
                            <div className="match-modal-matches">
                              {/* Paginated VGC matches */}
                              {group.matches.vgc
                                .slice(
                                  (currentVGCPage - 1) * matchesPerPage,
                                  currentVGCPage * matchesPerPage
                                )
                                .map(renderVGCMatch)}
                            </div>
                            
                            {/* Pagination controls for VGC */}
                            {group.matches.vgc.length > matchesPerPage && (
                              <div className="pagination-controls">
                                <button 
                                  className="pagination-button"
                                  onClick={() => handleVGCPageChange(Math.max(1, currentVGCPage - 1))}
                                  disabled={currentVGCPage === 1}
                                >
                                  <i className="fas fa-chevron-left"></i> Previous
                                </button>
                                <span className="pagination-info">
                                  Page {currentVGCPage} of {Math.ceil(group.matches.vgc.length / matchesPerPage)}
                                </span>
                                <button 
                                  className="pagination-button"
                                  onClick={() => handleVGCPageChange(Math.min(Math.ceil(group.matches.vgc.length / matchesPerPage), currentVGCPage + 1))}
                                  disabled={currentVGCPage === Math.ceil(group.matches.vgc.length / matchesPerPage)}
                                >
                                  Next <i className="fas fa-chevron-right"></i>
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="no-matches-message">No matches in this group</div>
                  )}
                </>
              )}
            </div>
            
            {/* Modal Footer */}
            <div className="match-modal-footer">
              <button className="btn btn-primary" onClick={handleCloseModal}>Close</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default MatchGroupItem;